import { AfterRenderRef, Component, ElementRef, HostListener, afterRender, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../services/dialog.service';
import { ButtonComponent } from '../button/button.component';

/**
 * Renders whatever dialog DialogService has active. Mounted once in
 * AppComponent, next to the toast container. z-[60] sits above the mobile
 * sidebar/backdrop (z-50). Escape and backdrop click cancel; the destructive
 * button is never autofocused (Cancel is, in danger mode).
 */
@Component({
  selector: 'app-dialog-host',
  standalone: true,
  imports: [FormsModule, ButtonComponent],
  template: `
    @if (dialogs.active(); as dialog) {
      <div
        class="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4"
        (click)="cancel()"
        data-testid="dialog-backdrop"
      >
        <!-- max-h + scroll: on a phone with the keyboard up (input/requireText
             dialogs always raise it) the visual viewport drops to ~350px, and
             a centred fixed-height dialog then overflowed past both edges with
             no way to scroll — putting the confirm button out of reach. 100dvh
             rather than 100vh so mobile browser chrome is excluded. -->
        <div
          class="bg-white rounded-xl shadow-xl border p-6 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="dialog.title"
          (click)="$event.stopPropagation()"
        >
          <h2 class="text-lg font-semibold text-gray-900 mb-2">{{ dialog.title }}</h2>

          @if (dialog.message) {
            <p class="text-sm text-gray-600 mb-4">{{ dialog.message }}</p>
          }

          @if (dialog.mode === 'input') {
            <label class="block text-sm font-medium text-gray-700 mb-1" for="dialog-input">{{ dialog.label }}</label>
            <input
              id="dialog-input"
              #dialogInput
              type="text"
              [(ngModel)]="inputValue"
              [placeholder]="dialog.placeholder ?? ''"
              [attr.maxlength]="dialog.maxlength ?? null"
              (keydown.enter)="confirm()"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 mb-4"
            />
          }

          @if (dialog.requireText) {
            <label class="block text-sm font-medium text-gray-700 mb-1" for="dialog-require">
              Type <span class="font-mono font-bold">{{ dialog.requireText }}</span> to confirm
            </label>
            <input
              id="dialog-require"
              type="text"
              [(ngModel)]="requireValue"
              [placeholder]="dialog.requireText"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
            />
          }

          <div class="flex justify-end gap-2">
            <app-button variant="secondary" (click)="cancel()">{{ dialog.cancelLabel }}</app-button>
            <app-button
              [variant]="dialog.variant"
              (click)="confirm()"
              [disabled]="confirmDisabled(dialog.requireText)"
            >
              {{ dialog.confirmLabel }}
            </app-button>
          </div>
        </div>
      </div>
    }
  `,
})
export class DialogHostComponent {
  readonly dialogs = inject(DialogService);
  private readonly host = inject(ElementRef<HTMLElement>);

  inputValue = '';
  requireValue = '';
  private focusedFor: unknown = null;
  // afterRender (not ngAfterViewInit) because the dialog appears/disappears
  // reactively — focus must run when the @if content materializes.
  private readonly focusRef: AfterRenderRef = afterRender(() => {
    const active = this.dialogs.active();
    if (!active || this.focusedFor === active) return;
    this.focusedFor = active;
    this.inputValue = '';
    this.requireValue = '';
    const root = this.host.nativeElement as HTMLElement;
    const input = root.querySelector<HTMLInputElement>('#dialog-input, #dialog-require');
    if (input) {
      input.focus();
    } else {
      // Never autofocus the destructive action — land on Cancel.
      const buttons = root.querySelectorAll<HTMLButtonElement>('button');
      buttons[buttons.length - 2]?.focus();
    }
  });

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.dialogs.active()) this.cancel();
  }

  confirmDisabled(requireText?: string): boolean {
    if (requireText && this.requireValue !== requireText) return true;
    if (this.dialogs.active()?.mode === 'input' && !this.inputValue.trim()) return true;
    return false;
  }

  confirm(): void {
    const dialog = this.dialogs.active();
    if (!dialog || this.confirmDisabled(dialog.requireText)) return;
    this.dialogs.settle(dialog.mode === 'input' ? this.inputValue.trim() : true);
  }

  cancel(): void {
    const dialog = this.dialogs.active();
    if (!dialog) return;
    this.dialogs.settle(dialog.mode === 'input' ? null : false);
  }
}
